import { useCallback, useMemo } from 'react';
import { filter, isEmpty, isNil, map } from 'lodash';
import { useStore } from 'common/store';
import { Rows } from 'common/types/scalarSpreadsheet';
import { UseStoreValues } from 'common/types/store';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import {
  FIRM_SUMMARY_SPREADSHEET_EXCLUDED_ROWS_COUNT,
  FIRM_SUMMARY_SPREADSHEET_NAME,
  FIRM_SUMMARY_SPREADSHEET_TABLE_TERMS,
} from 'pages/Firms/common/constants';
import { useCellCustomClasses } from 'pages/Funds/hooks';
import { getObjectValue } from 'utilities';
import { createColumns, createParser, createRows, FirmSummaryColumn, FirmSummaryParser } from './config';
import {
  FirmSummaryCellData,
  FirmSummaryInitialObject,
  FirmSummarySpreadsheet,
  FirmSummaryTableData,
  ParentFundByCompany,
  UseFirmSummarySheetParams,
  UseFirmSummarySheetResult,
} from './useFirmSummarySheet.types';

const useFirmSummarySheet = (params: UseFirmSummarySheetParams): UseFirmSummarySheetResult => {
  const { fundsAvailable, fundsByCompany, isAllFundsSelected, selectedFund, setSelectedFund } = params;

  const [storeValue] = useStore() as UseStoreValues;
  const { firmInfo: currentFirm } = getObjectValue(storeValue);
  const { slug: currentFirmSlug } = getObjectValue(currentFirm);

  const { cellCustomClasses: firmSummaryClassNames } = useCellCustomClasses();

  const parentFundsByCompany = useMemo<ParentFundByCompany[]>(
    () =>
      filter(
        map(
          fundsByCompany,
          (fund, rowNumber) =>
            ({
              isDraft: !fund.is_published && !fund.is_final,
              // Parent or standalone | when filtering by fund, all rows are parent
              isParent: isAllFundsSelected ? fund.is_parent || (!fund.is_parent && isNil(fund.parent_key)) : true,
              rowNumber: rowNumber + FIRM_SUMMARY_SPREADSHEET_EXCLUDED_ROWS_COUNT + 1, // +1 for 0-based index
            }) as ParentFundByCompany
        ),
        fund => fund.isParent
      ),
    [fundsByCompany, isAllFundsSelected]
  );

  const parentFundsByCompanyRows = useMemo<number[]>(
    () => map(parentFundsByCompany, 'rowNumber'),
    [parentFundsByCompany]
  );

  const nonDraftParentFundsByCompanyRows = useMemo<number[]>(
    () =>
      map(
        filter(parentFundsByCompany, row => !row.isDraft),
        'rowNumber'
      ),
    [parentFundsByCompany]
  );

  const rows = useMemo<Rows>(
    () => createRows({ firmSlug: currentFirmSlug, fundsByCompany, isAllFundsSelected }),
    [currentFirmSlug, fundsByCompany, isAllFundsSelected]
  );

  const columns = useMemo<FirmSummaryColumn[]>(() => {
    if (!isEmpty(fundsByCompany)) {
      return createColumns({ fundsByCompany });
    }

    return [];
  }, [fundsByCompany]);

  const parser: FirmSummarySpreadsheet['parser'] = useCallback(
    (parserParams: FirmSummaryParser) =>
      createParser({
        ...parserParams,
        firmSummaryClassNames,
        fundsAvailable,
        nonDraftParentFundsByCompanyRows,
        parentFundsByCompanyRows,
        selectedFund,
        setSelectedFund,
      }),
    [
      firmSummaryClassNames,
      fundsAvailable,
      nonDraftParentFundsByCompanyRows,
      parentFundsByCompanyRows,
      selectedFund,
      setSelectedFund,
    ]
  );

  // Spreadsheet config
  const spreadsheet = useMemo<FirmSummarySpreadsheet | null>(() => {
    if (!isEmpty(columns)) {
      const spreadsheetConfig: FirmSummarySpreadsheet = new SpreadsheetConfig<
        FirmSummaryInitialObject,
        FirmSummaryColumn,
        FirmSummaryTableData,
        FirmSummaryCellData
      >({
        allowConfirmAndDeleteColumn: false,
        allowDeleteRow: true,
        alwaysDisplayLegend: false,
        colTitleRow: 0,
        columns,
        currencyFormatter: true,
        format: undefined,
        hasColTitle: true,
        name: FIRM_SUMMARY_SPREADSHEET_NAME,
        page: FIRM_SUMMARY_SPREADSHEET_NAME,
        parser,
        reverseParser: undefined,
        rowConfig: rows,
        showPreviousColsDivider: false,
        showTitlesColumn: true,
        showToolbar: true,
        showTotalColumn: false,
        tableData: { columns },
        tableTerms: FIRM_SUMMARY_SPREADSHEET_TABLE_TERMS,
        totalParser: undefined,
        unitsFormatter: true,
      });

      return spreadsheetConfig;
    }

    return null;
  }, [columns, parser, rows]);

  return {
    spreadsheet,
  };
};

export default useFirmSummarySheet;
